"use client";
import React from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import commentsApi from "@/api/comments/feed/client";
import Comment from "..";
import { PostCommentDto } from "@/api/comments/feed/types";
import usePostCommentLike from "../../../Likes/hooks/usePostCommentLike";
import useMe from "@/shared/hooks/useMe";

export interface Props {
  feedPostId: string;
  comment: PostCommentDto;
  replyTo?: string;
}

export default function FeedPostComment({ feedPostId, comment }: Props) {
  const queryClient = useQueryClient();

  const { me } = useMe();

  const { mutateAsync: createPostComment } = useMutation({
    mutationFn: (text: string) =>
      commentsApi.createPostComment({
        text,
        repliedToPostId: feedPostId,
        repliedToUserId: comment.author.id,
        repliedToCommentId: comment.id,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries(["feed-comments", feedPostId]);
      queryClient.invalidateQueries(["feed-comment-count", feedPostId]);
    },
  });

  const { mutateAsync: deletePostComment } = useMutation({
    mutationFn: commentsApi.deletePostComment,
    onSuccess: () => {
      queryClient.invalidateQueries(["feed-comments", feedPostId]);
      queryClient.invalidateQueries(["feed-comment-count", feedPostId]);
    },
  });

  const { isLikedByMe, toggleLike, likeCount } = usePostCommentLike({
    commentId: comment.id,
    user: me,
  });

  return (
    <Comment
      comment={comment}
      onPost={async (text: string) => await createPostComment(text)}
      onDelete={async (id: string) => await deletePostComment(id)}
      iconsSize="big"
      likeState={{
        isLikedByMe,
        toggleLike,
        likeCount,
      }}
      className="border-none p-0"
      disabled={!me}
    />
  );
}
